import { useState } from "react";
import Layout from "@/components/feature/Layout";
import PageHero from "@/components/feature/PageHero";
import CtaBanner from "@/components/feature/CtaBanner";
import Seo from "@/components/feature/Seo";
import SectionHeading from "@/components/base/SectionHeading";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Lightbox from "@/pages/gallery/components/Lightbox";
import { galleryItems } from "@/data/gallery";

// Show "All" first, then only the categories that actually contain images.
const presentCategories = Array.from(new Set(galleryItems.map((item) => item.category)));
const orderedCategories = ["Clinic", "Doctors", "Facilities", "Treatments", "Events", "Patient Care"].filter(
  (category) => presentCategories.includes(category as (typeof presentCategories)[number])
);
const categories = ["All", ...orderedCategories];

export default function Gallery() {
  const [active, setActive] = useState("All");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const visible = active === "All" ? galleryItems : galleryItems.filter((item) => item.category === active);

  return (
    <Layout>
      <Seo
        title="Gallery | Ortho Spine Care, Trichy"
        description="Photographs from Ortho Spine Care in Srirangam, Trichy — consultations, clinical examinations and patient care at our orthopaedic and spine practice."
        path="/gallery"
      />

      <PageHero
        title="Gallery"
        subtitle="A look inside our practice — consultations, clinical examinations and the patient care that happens every day."
        crumbs={[{ label: "Home", to: "/" }, { label: "Gallery" }]}
      />

      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeading
            eyebrow="Our practice"
            title="Moments from the clinic"
            description="Browse by category, and select any image to view it larger."
          />

          <div className="mt-8 flex justify-center">
            <Tabs value={active} onValueChange={setActive}>
              <TabsList>
                {categories.map((category) => (
                  <TabsTrigger key={category} value={category}>
                    {category}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {visible.length ? (
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((item, itemIndex) => (
                <button
                  key={item.title}
                  type="button"
                  onClick={() => setLightboxIndex(itemIndex)}
                  className="group reveal relative cursor-pointer overflow-hidden rounded-lg border border-background-200 bg-background-100 text-left"
                  aria-label={`View larger image: ${item.title}`}
                >
                  <div className="h-64 w-full overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.alt}
                      title={`${item.title} — Ortho Spine Care`}
                      className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 bg-background-50 px-4 py-3">
                    <span className="font-heading text-sm font-semibold text-foreground-950">{item.title}</span>
                    <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-primary-100 text-primary-700">
                      <i className="ri-zoom-in-line text-sm" aria-hidden="true"></i>
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100/60 p-10 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                <i className="ri-image-line text-2xl" aria-hidden="true"></i>
              </span>
              <p className="mt-4 font-heading text-base font-semibold text-foreground-950">
                Photos for this category are coming soon
              </p>
              <p className="mt-2 font-body text-sm text-foreground-600">
                New images are added to this gallery regularly.
              </p>
            </div>
          )}
        </div>
      </section>

      <CtaBanner
        title="Would you like to visit us?"
        text="Book a consultation and meet the team at Ortho Spine Care in Srirangam, Trichy."
      />

      <Lightbox
        items={visible}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={(nextIndex) => setLightboxIndex(nextIndex)}
      />
    </Layout>
  );
}